import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import useChartTheme from 'components/StudioCharts/useChartTheme';
import StudioEChartContainer, {
  canDrawWithECharts,
  isDrawnOnlyWithECharts,
  useIsEChartsRenderer,
} from 'components/StudioCharts';
import V5Gadget from 'components/V5Gadget';
import DashboardContextMenuProvider from 'contextProviders/DashboardContextMenuProvider';
import CardContext from 'contexts/CardContext';
import ChartForecastContext, {
  ChartForecast,
} from 'contexts/ChartForecastContext';
import ChartOptionsTransformContext, {
  ChartOptionsTransform,
} from 'contexts/ChartOptionsTransformContext';
import ComparisonContext from 'contexts/ComparisonContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import useValueFormatters from 'hooks/useValueFormatters';

import buildChartDefinition from './buildChartDefinition';
import {
  CompositeChartPopup,
  CompositeChartPopupButton,
  CompositeChartPopupProvider,
} from './CompositeCard/compositeChartPopup';
import { getChartStyle } from './chartDisplayOptions';
import { NO_DRAG_CLASS } from './constants';
import { toDimension } from './dimensions';
import ChartPlaceholder from './ChartPlaceholder';
import { hasChartStyle, withChartStyle } from './chartStyle';
import { FORECAST_CLASS_NAMES, canForecast, withForecast } from './forecast';
import getMetricDataTypes from './getMetricDataTypes';
import { getResolvedMetricIds } from './seriesVariants';
import StudioChartMenu, { StudioChartDrillDown } from './StudioChartMenu';
import useRenderedComparison from './useRenderedComparison';
import { getVisType, isEChartsOnly, isTimelineHeatmap } from './visTypes';

// Hosts a V5 gadget. The gadget fills an absolutely-positioned box so
// Highcharts gets an explicit size; its own title bar and date footer are
// hidden in favour of the block's header and Studio's global date range.
// Isolated because Chart raises itself to Z_INDEX.CHART (300), which would
// otherwise paint it over the Composite row toolbar and the block's resize
// handles. Charts in unselected blocks ignore the pointer, so they show no
// tooltips (or drill-downs) and a press falls through to select the block.
const ChartBody = styled.div<{ $isInteractive: boolean }>`
  position: relative;
  isolation: isolate;
  flex: 1;
  min-height: 0;

  > div {
    position: absolute;
    inset: 0;
    pointer-events: ${(props) => (props.$isInteractive ? 'auto' : 'none')};
  }

  .canvas-card-header,
  .canvas-card-footer,
  .autoHideTrigger {
    display: none;
  }

  /* A forecast reads as an estimate: its range a fill without edges (the
     global chart styles draw every line 3px), its line thinner and fainter,
     and its period shaded after a faint start line */
  .${FORECAST_CLASS_NAMES.band} .highcharts-graph {
    stroke-width: 0;
  }
  .${FORECAST_CLASS_NAMES.line} .highcharts-graph {
    stroke-width: 2px;
    stroke-opacity: 0.7;
  }
  .${FORECAST_CLASS_NAMES.period} {
    fill: var(--color-bg-secondary);
  }
  .${FORECAST_CLASS_NAMES.start} {
    stroke: var(--color-border-primary);
  }
`;

// Shown instead of the chart until it has what it needs (see
// buildChartDefinition)
const withoutLegend: ChartOptionsTransform = (options) => ({
  ...options,
  legend: { ...options.legend, enabled: false },
});

// Chart options that change only how the chart is drawn, not what it
// fetches
const DRAWN_ONLY_OPTIONS: (keyof Studio.ChartOptions)[] = [
  'isLegendHidden',
  'curve',
  'lineFill',
  'hasBarBackground',
  'isBarGradient',
  'pictorial',
  'map',
];

// A chart for a chart block or a Composite block's chart element. w and h are
// the grid size of the block it sits in; the chart only responds to the
// pointer (tooltips etc.) while that block is selected.
const StudioChart = ({
  content,
  name,
  w,
  h,
  isInteractive,
  isOnView = false,
  hasContextMenu = false,
  popup,
  drillDownMenu,
}: {
  content: Studio.ChartContent;
  name: string;
  w: number;
  h: number;
  isInteractive: boolean;
  // A gadget's block on a dashboard or report, inside its gadget's providers
  // (see useRenderedComparison)
  isOnView?: boolean;
  // Clicking a point opens the dashboard chart menu for it, as a gadget's
  // does: for a Composite's chart on a dashboard, which has no gadget (and
  // so no menu) of its own
  hasContextMenu?: boolean;
  // A Composite's chart on a dashboard: the popup it opens, from an icon
  // over its corner and its menu
  popup?: CompositeChartPopup;
  // In Studio, on a dashboard: clicking a point shows the chart's popup (or
  // that it has none), and opens its Drill-down settings
  drillDownMenu?: StudioChartDrillDown;
}) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { getFieldLabel } = useGetFieldLabel();
  const { formatMetric } = useValueFormatters();
  const visType = getVisType(content.visType);

  // Keyed on the fields that shape the chart, so moving/resizing the block
  // (which creates a new object) doesn't rebuild it and refetch
  const metricIdsKey = (content.metricIds ?? []).join(',');
  const seriesVariantsKey = content.seriesVariants
    ? JSON.stringify(content.seriesVariants)
    : '';
  const dimensionsKey = (content.dimensions ?? []).join(',');
  const dimensionOptionsKey = content.dimensionOptions
    ? JSON.stringify(content.dimensionOptions)
    : '';
  const tableKey = content.table ? JSON.stringify(content.table) : '';
  // The legend and the style are drawn onto the chart (see withoutLegend,
  // withChartStyle), so changing them doesn't rebuild the chart and refetch
  const chartOptionsKey = content.chart
    ? JSON.stringify(_.omit(content.chart, [...DRAWN_ONLY_OPTIONS]))
    : '';
  const trendPeriod = content.trend ? content.trend.period : undefined;
  const colorsKey = content.chartColors
    ? JSON.stringify(content.chartColors)
    : '';
  const chart = useMemo(
    () =>
      buildChartDefinition({
        card: {
          id: content.id,
          visType: content.visType,
          description: content.description,
          metricIds: metricIdsKey ? metricIdsKey.split(',') : [],
          seriesVariants: seriesVariantsKey
            ? JSON.parse(seriesVariantsKey)
            : undefined,
          dimensions: dimensionsKey ? dimensionsKey.split(',') : [],
          dimensionOptions: dimensionOptionsKey
            ? JSON.parse(dimensionOptionsKey)
            : undefined,
          table: tableKey ? JSON.parse(tableKey) : undefined,
          chart: chartOptionsKey ? JSON.parse(chartOptionsKey) : undefined,
          chartColors: colorsKey ? JSON.parse(colorsKey) : undefined,
          trend: trendPeriod ? { period: trendPeriod } : undefined,
        },
        name,
      }),
    [
      chartOptionsKey,
      colorsKey,
      content.description,
      content.id,
      content.visType,
      dimensionOptionsKey,
      dimensionsKey,
      metricIdsKey,
      name,
      seriesVariantsKey,
      tableKey,
      trendPeriod,
    ],
  );
  const cardContextValue = useMemo(
    () =>
      'chartDefinition' in chart
        ? { i: content.id, w, h, chartDefinition: chart.chartDefinition }
        : undefined,
    [chart, content.id, h, w],
  );

  // Tables and unsplit Lines compare against their own period, or follow the
  // report's. Every block gets its own (usually empty) comparison, so none
  // leaks in from around the canvas.
  const comparison = useRenderedComparison({ content, isOnView });

  // The chart's query predicts the periods ahead (and refetches when they
  // change); its predicted rows come back here, kept out of the gadget's own
  // data, and are drawn onto its final options. A compared chart can't show
  // them, so doesn't ask.
  const forecastPeriods =
    content.forecast && canForecast(content) && !comparison
      ? content.forecast.periods
      : undefined;
  const isRangeHidden = !!(content.forecast && content.forecast.isRangeHidden);
  const [predicted, setPredicted] = useState<MetricsResponse>([]);
  const onPredictedRows = useCallback(
    (rows: MetricsResponse) =>
      setPredicted((current) => (_.isEqual(current, rows) ? current : rows)),
    [],
  );
  // Turned off, the last predictions go (so they don't flash back on)
  useEffect(() => {
    if (!forecastPeriods) {
      setPredicted([]);
    }
  }, [forecastPeriods]);
  const forecastContextValue = useMemo(
    (): ChartForecast | undefined =>
      forecastPeriods
        ? { periods: forecastPeriods, onPredictedRows }
        : undefined,
    [forecastPeriods, onPredictedRows],
  );
  // Any transform from around the chart (e.g. a Composite linking its
  // Numbers to the series) applies after the forecast, so it sees the
  // forecast's series too
  const outerTransform = useContext(ChartOptionsTransformContext);
  // Lines and bars styled as the block says, before a forecast is added so
  // its series keep their own look
  const theme = useChartTheme();
  const chartStyleKey = JSON.stringify(
    getChartStyle({
      id: content.id,
      visType: content.visType,
      chart: content.chart,
      seriesVariants: content.seriesVariants,
    }),
  );
  const chartStyle = useMemo(
    (): Studio.ChartStyle => JSON.parse(chartStyleKey),
    [chartStyleKey],
  );
  const styleTransform = useMemo(
    (): ChartOptionsTransform | undefined =>
      hasChartStyle(chartStyle)
        ? (options) =>
            withChartStyle({
              options,
              style: chartStyle,
              trackColor: theme.track,
            })
        : undefined,
    [chartStyle, theme.track],
  );
  const isEChartsRenderer = useIsEChartsRenderer();
  // A Map's area and look, drawn onto the chart as its style is
  const mapKey =
    content.visType === 'regionMap'
      ? JSON.stringify((content.chart && content.chart.map) ?? {})
      : '';
  const map = useMemo(
    (): Studio.MapOptions | undefined =>
      mapKey ? JSON.parse(mapKey) : undefined,
    [mapKey],
  );
  // A Map's second dimension, which its regions' tooltips break down by
  const mapBreakdownKey = useMemo(() => {
    const [dimA, dimB] = dimensionsKey ? dimensionsKey.split(',') : [];
    if (!mapKey || !dimB || dimB === dimA) {
      return '';
    }
    const dimensionOptions: Studio.DimensionOptionsLookup | undefined =
      dimensionOptionsKey ? JSON.parse(dimensionOptionsKey) : undefined;
    return JSON.stringify(
      toDimension({
        field: dimB,
        options: dimensionOptions ? dimensionOptions[dimB] : undefined,
      }),
    );
  }, [dimensionOptionsKey, dimensionsKey, mapKey]);
  const mapBreakdown = useMemo(
    (): Dimension | undefined =>
      mapBreakdownKey ? JSON.parse(mapBreakdownKey) : undefined,
    [mapBreakdownKey],
  );
  const forecastTransform = useMemo(
    (): ChartOptionsTransform | undefined =>
      forecastPeriods && predicted.length > 0
        ? (options) =>
            withForecast({
              options,
              predicted,
              formatValue: formatMetric,
              showRange: !isRangeHidden,
            })
        : undefined,
    [forecastPeriods, formatMetric, isRangeHidden, predicted],
  );
  // A hidden legend applies last, so nothing brings it back
  const isLegendHidden = !!(content.chart && content.chart.isLegendHidden);
  // The same forecast, for ECharts to draw into its chart model
  const echartsForecast = useMemo(
    () =>
      forecastPeriods && predicted.length > 0
        ? { predicted, showRange: !isRangeHidden }
        : undefined,
    [forecastPeriods, isRangeHidden, predicted],
  );
  const chartTransform = useMemo((): ChartOptionsTransform | undefined => {
    const transforms = [
      styleTransform,
      forecastTransform,
      outerTransform,
      isLegendHidden ? withoutLegend : undefined,
    ].filter((t): t is ChartOptionsTransform => !!t);
    return transforms.length > 0
      ? (options) => transforms.reduce((next, t) => t(next), options)
      : undefined;
  }, [forecastTransform, isLegendHidden, outerTransform, styleTransform]);
  const comparisonContextValue = useMemo(
    () => ({ currentComparison: comparison }),
    [comparison],
  );

  const dimensionsLabel = useMemo(() => {
    if (!dimensionsKey) {
      return '';
    }
    const [dataType] = getMetricDataTypes({
      metricIds: getResolvedMetricIds({
        metricIds: metricIdsKey ? metricIdsKey.split(',') : [],
        seriesVariants: seriesVariantsKey
          ? JSON.parse(seriesVariantsKey)
          : undefined,
      }),
      metricOptionsLookup,
    });
    return `by ${dimensionsKey
      .split(',')
      .map((field) => (dataType ? getFieldLabel({ field, dataType }) : field))
      .join(', ')}`;
  }, [
    dimensionsKey,
    getFieldLabel,
    metricIdsKey,
    metricOptionsLookup,
    seriesVariantsKey,
  ]);

  if (cardContextValue) {
    // ECharts draws what the Highcharts transforms add (style, forecast,
    // a Composite's legend link) from its own settings, so they don't reach
    // it. Pictorial Bars, Maps, Timeline heatmaps and Bullets only it can
    // draw, whatever the gate says.
    const isECharts =
      isEChartsOnly(content.visType) ||
      isTimelineHeatmap(content) ||
      isDrawnOnlyWithECharts(cardContextValue.chartDefinition) ||
      (isEChartsRenderer &&
        canDrawWithECharts(cardContextValue.chartDefinition));
    // A press on a grid block starts dragging it, which swallows the click
    // a point needs (see StudioCanvas), so a chart taking clicks isn't a drag
    // handle
    const isClickable = !!drillDownMenu && isInteractive;
    const body = (
      <ChartBody
        className={isClickable ? NO_DRAG_CLASS : undefined}
        $isInteractive={isInteractive}
        data-testid={`studio-card-chart-${content.id}`}
        data-interactive={isInteractive}
        data-forecast={forecastPeriods ?? 0}
        data-predicted={forecastPeriods ? predicted.length : 0}
        data-renderer={isECharts ? 'echarts' : 'highcharts'}
      >
        <ComparisonContext.Provider value={comparisonContextValue}>
          <ChartForecastContext.Provider value={forecastContextValue}>
            <ChartOptionsTransformContext.Provider value={chartTransform}>
              {isECharts ? (
                <StudioEChartContainer
                  chartDefinition={cardContextValue.chartDefinition}
                  isLegendHidden={isLegendHidden}
                  chartStyle={chartStyle}
                  map={map}
                  mapBreakdown={mapBreakdown}
                  forecast={echartsForecast}
                  useSmallNoDataMessage
                />
              ) : (
                <V5Gadget
                  chartDefinition={cardContextValue.chartDefinition}
                  useSmallNoDataMessage
                />
              )}
            </ChartOptionsTransformContext.Provider>
          </ChartForecastContext.Provider>
        </ComparisonContext.Provider>
      </ChartBody>
    );
    // The menu sits beside the chart's box (whose children are pinned to
    // it), so it fills the cell the chart sits in, and under its card
    // context, so it knows the chart it was opened from
    return (
      <CardContext.Provider value={cardContextValue}>
        <CompositeChartPopupProvider popup={popup}>
          {drillDownMenu ? (
            <StudioChartMenu drillDown={drillDownMenu}>{body}</StudioChartMenu>
          ) : hasContextMenu ? (
            <DashboardContextMenuProvider>{body}</DashboardContextMenuProvider>
          ) : (
            body
          )}
          <CompositeChartPopupButton />
        </CompositeChartPopupProvider>
      </CardContext.Provider>
    );
  }

  return (
    <ChartPlaceholder
      cardId={content.id}
      visType={visType.id}
      message={
        'missing' in chart
          ? [chart.missing, dimensionsLabel].filter(Boolean).join(' · ')
          : ''
      }
    />
  );
};

export default StudioChart;
